/**
 * Client-side utilities for managing yzsa (anonymous ID) with cookie and localStorage support
 */

import Cookies from "js-cookie";

const YZSA_COOKIE_NAME = "yzsa";
const YZSA_LOCALSTORAGE_KEY = "yzsa";

// Cache for YZSA value to avoid repeated localStorage reads
let yzsaCache: string | undefined | null = null;
let cacheInitialized = false;

/**
 * Generates a random hex string for anonymous ID
 * @param length - Length of the hex string (default: 16)
 * @returns Random hex string
 */
export function generateRandomHex(length = 16): string {
  const chars = "0123456789abcdef";
  let result = "";
  for (let i = 0; i < length * 2; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Clears the YZSA cache, forcing a fresh read on next getYzsaId() call
 * Useful when YZSA value changes (e.g., after sync)
 */
export function clearYzsaCache(): void {
  yzsaCache = null;
  cacheInitialized = false;
}

/**
 * Gets the yzsa anonymous ID, first from cache, then localStorage, then cookie
 * @param forceRefresh - If true, bypasses cache and reads from storage (default: false)
 * @returns The anonymous ID or undefined if not found
 */
export function getYzsaId(forceRefresh = false): string | undefined {
  // Only run on client side
  if (typeof window === "undefined") {
    return undefined;
  }

  // Return cached value if available and not forcing refresh
  if (!forceRefresh && cacheInitialized) {
    return yzsaCache || undefined;
  }

  try {
    const yzsaLocalStorageValue = localStorage.getItem(YZSA_LOCALSTORAGE_KEY);
    const cookieValue = Cookies.get(YZSA_COOKIE_NAME);

    if (yzsaLocalStorageValue) {
      console.info("[YZSA] Using value from localStorage");

      if (yzsaLocalStorageValue !== cookieValue) {
        // Set cookie with localStorage value if it's different from the cookie value
        Cookies.set(YZSA_COOKIE_NAME, yzsaLocalStorageValue, {
          expires: 365, // 365 days
          path: "/",
          sameSite: "lax",
        });
      }

      // Cache the value
      yzsaCache = yzsaLocalStorageValue;
      cacheInitialized = true;
      return yzsaLocalStorageValue;
    }

    if (cookieValue) {
      // If we have a cookie value (and it's not in localStorage),
      // also save it to localStorage for future fallback
      try {
        localStorage.setItem(YZSA_LOCALSTORAGE_KEY, cookieValue);
      } catch (error) {
        console.warn(
          "[YZSA] Failed to save cookie value to localStorage:",
          error,
        );
      }

      // Cache the value
      yzsaCache = cookieValue;
      cacheInitialized = true;
      return cookieValue;
    }

    // No value found, cache this result
    yzsaCache = undefined;
    cacheInitialized = true;
  } catch (error) {
    console.warn("[YZSA] Failed to read anonymous ID:", error);
  }

  return undefined;
}
